import { LinkIcon } from 'lucide-react'

import { formatPercentage } from '@/domain/common/format'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { referralBoostPercentage } from '@/features/points/consts'
import {
  buildReferralUrl,
  UseReferralShareDialogResult,
} from '@/features/points/dialogs/referral-share-dialog/logic/useReferralShareDialog'
import { selectSeasonValue } from '@/features/points/helpers'
import { assets } from '@/ui/assets'
import { Button, ButtonIcon } from '@/ui/atoms/button/Button'
import { ExternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { Panel } from '@/ui/atoms/panel/Panel'

export function ReferralShareView({
  referralCode,
  season2TotalPoints,
  season3TotalPoints,
  pointsSeason,
  invitedUsers,
  shareUrl,
  onCopy,
  onShare,
}: UseReferralShareDialogResult) {
  const label = selectSeasonValue(pointsSeason, {
    2: 'Season 2 points',
    3: 'Season 3 points',
  })
  const totalPoints = selectSeasonValue(pointsSeason, {
    2: season2TotalPoints,
    3: season3TotalPoints,
  })

  return (
    <div className="flex flex-col gap-8 p-8">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 md:mb-2">
          <h5 className="typography-heading-5">Refer a friend</h5>
          <span className="typography-body-3 text-secondary">
            You will earn {formatPercentage(referralBoostPercentage, { minimumFractionDigits: 0 })} of the points your
            invitee earns.
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <span className="typography-label-3 text-secondary">Your referral link</span>
          <Panel variant="tertiary" spacing="s">
            <FormattedLink referralCode={referralCode} />
          </Panel>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between">
            <span className="typography-label-3 text-secondary">Invited users</span>
            <span className="typography-label-3">{invitedUsers}</span>
          </div>
          <div className="flex justify-between">
            <span className="typography-label-3 text-secondary">{label}</span>
            <div className="flex items-center gap-0.5 md:gap-1.5">
              <img src={assets.points.pointsIcon} alt="Points Icon" className="icon-size-xs" />
              <div className="typography-label-3 text-primary">
                {USD_MOCK_TOKEN.format(totalPoints, { style: 'auto' })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Actions onCopy={onCopy} shareUrl={shareUrl} onShare={onShare} />
    </div>
  )
}

function FormattedLink({ referralCode }: Pick<UseReferralShareDialogResult, 'referralCode'>) {
  return (
    <>
      <span className="typography-label-2 text-secondary">{buildReferralUrl('')}</span>
      <span className="typography-label-2">{referralCode.toUpperCase()}</span>
    </>
  )
}

function Actions({ onCopy, shareUrl, onShare }: Pick<UseReferralShareDialogResult, 'onCopy' | 'shareUrl' | 'onShare'>) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:justify-between md:gap-4">
      <ExternalLinkButton to={shareUrl} variant="secondary" size="l" className="w-full" onClick={onShare}>
        <img src={assets.socialPlatforms.xNoBackground} alt="X logo" className="icon-size-sm" />
        Share on X
      </ExternalLinkButton>
      <Button variant="primary" size="l" className="w-full" onClick={onCopy}>
        <ButtonIcon icon={LinkIcon} />
        Copy link
      </Button>
    </div>
  )
}
